import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Building2 } from 'lucide-react';
import { addDays, addMonths, isoWeek, monthEnd, monthStart, weekStart } from '@pz/core';
import { AreaBars } from '../components/areas';
import { StackBars, WeekBars } from '../components/charts';
import { Button, EmptyState, ErrorBox, Gauge, Input, Loading, matchesSearch, PageHeader, Panel, Pill, Select, Stat, Tabs, td, th } from '../components/ui';
import { d, h, hh, pct } from '../lib/format';
import { useQ, useTabParam } from '../lib/hooks';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { useMe } from '../lib/me';

type Preset = 'last4w' | 'last3m' | 'thisMonth' | 'lastMonth' | 'year' | 'last12m';
const PRESETS: Preset[] = ['last4w', 'last3m', 'thisMonth', 'lastMonth', 'year', 'last12m'];

/**
 * Period of the cockpit and the staff review. Rolling periods (last 4 weeks, last 3 months) show figures right after
 * the turn of a month or quarter, where "this month" or "this quarter" would be nearly empty. The weeks start on Monday.
 */
export function useRange(initial: Preset) {
  const { me } = useMe();
  const [preset, setPreset] = useState<Preset>(initial);
  const today = me.today;
  const m = monthStart(today);
  const range =
    preset === 'last4w'
      ? { from: addDays(weekStart(today), -21), to: today }
      : preset === 'last3m'
        ? { from: addDays(addMonths(today, -3), 1), to: today }
        : preset === 'thisMonth'
          ? { from: m, to: monthEnd(m) }
          : preset === 'lastMonth'
            ? { from: addMonths(m, -1), to: monthEnd(addMonths(m, -1)) }
            : preset === 'year'
              ? { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` }
              : { from: addDays(addMonths(today, -12), 1), to: today };
  return { preset, setPreset, ...range };
}

export function RangeButtons({ r }: { r: ReturnType<typeof useRange> }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label={t('common.period')}>
      {PRESETS.map((p) => (
        <Button key={p} size="sm" variant={r.preset === p ? 'primary' : 'secondary'} aria-pressed={r.preset === p} onClick={() => r.setPreset(p)}>
          {t(`cockpit.ranges.${p}`)}
        </Button>
      ))}
    </div>
  );
}

/**
 * Cockpit for leading roles: all projects of the own scope on one page (overview, project table).
 * Project details stay on the project page, planning under "Planung", approvals under "Freigaben",
 * personal figures of the staff under "Mitarbeiterauswertung" (management only).
 */
export function CockpitPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const [tab, setTab] = useTabParam(['overview', 'projects'] as const);
  const r = useRange('last3m');
  const navigate = useNavigate();
  if (!me.cockpit) return <EmptyState>{t('cockpit.noAccess')}</EmptyState>;
  return (
    <div>
      <PageHeader
        title={t('cockpit.title')}
        subtitle={me.cockpit === 'lead' ? t('cockpit.subtitleLead') : t('cockpit.subtitleAll')}
        actions={
          <>
            <RangeButtons r={r} />
            {me.analysis && (
              <Button size="sm" icon={<Building2 className="size-3.5" />} onClick={() => navigate('/analysis')}>
                {t('cockpit.toAnalysis')}
              </Button>
            )}
          </>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('cockpit.tabs.overview') },
          { id: 'projects', label: t('cockpit.tabs.projects') },
        ]}
      />
      {tab === 'overview' && <OverviewTab from={r.from} to={r.to} />}
      {tab === 'projects' && <ProjectsTab from={r.from} to={r.to} />}
    </div>
  );
}

function OverviewTab({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const q = useQ('cockpit.overview', { from, to });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const x = q.data;
  const top = [...x.projects].filter((p) => p.periodMinutes > 0).sort((a, b) => b.periodMinutes - a.periodMinutes);
  const shown = top.slice(0, 12);
  const rest = top.slice(12);
  const alerts = x.projects.filter((p) => !p.archived && ((Number.isFinite(p.budgetUsed) && p.budgetUsed >= 0.8) || p.overPlanTasks > 0));
  return (
    <div className="flex flex-col gap-4">
      <p className="num text-xs text-graphite">{t('kpi.asOf', { from: d(x.since), to: d(x.asOf) })}</p>
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-4 xl:grid-cols-8">
        <Stat label={t('cockpit.booked')} value={hh(x.totals.minutes, 0)} />
        <Stat label={t('kpi.billableShare')} value={pct(x.totals.minutes ? x.totals.billable / x.totals.minutes : Number.NaN)} hint={hh(x.totals.billable, 0)} />
        <Stat label={t('cockpit.activeProjects')} value={x.totals.activeProjects} />
        <Stat label={t('cockpit.persons')} value={x.totals.persons} />
        <Stat label={t('cockpit.approvalsPending')} value={x.totals.approvalsPending} tone={x.totals.approvalsPending > 0 ? 'warn' : undefined} />
        <Stat label={t('cockpit.budgetAlerts')} value={x.totals.budgetAlerts} tone={x.totals.budgetAlerts > 0 ? 'warn' : undefined} />
        <Stat label={t('cockpit.overPlan')} value={x.totals.overPlanTasks} tone={x.totals.overPlanTasks > 0 ? 'warn' : undefined} />
        {x.totals.unbilledMinutes !== null && <Stat label={t('cockpit.unbilled')} value={hh(x.totals.unbilledMinutes, 0)} />}
      </div>
      <div className="grid gap-4 xl:grid-cols-5">
        <Panel title={t('cockpit.hoursByProject')} className="xl:col-span-3">
          {shown.length === 0 ? (
            <p className="text-sm text-graphite">{t('common.noData')}</p>
          ) : (
            <StackBars
              head={t('common.project')}
              series={[
                { key: 'billable', label: t('billingType.billable'), kind: 'strong' },
                { key: 'included', label: t('billingType.included'), kind: 'mid' },
                { key: 'nonBillable', label: t('billingType.non_billable'), kind: 'soft' },
              ]}
              rows={[
                ...shown.map((p) => ({
                  id: p.id,
                  label: `${p.number} ${p.name}`,
                  sub: p.budgetMinutes ? t('cockpit.budgetUsedShort', { value: pct(p.budgetUsed) }) : undefined,
                  values: { billable: p.periodBillable, included: p.periodIncluded, nonBillable: p.periodNonBillable },
                  onClick: () => navigate(`/projects/${p.id}?tab=kpi`),
                })),
                ...(rest.length
                  ? [
                      {
                        id: 'rest',
                        label: t('cockpit.otherProjects', { count: rest.length }),
                        values: {
                          billable: rest.reduce((s, p) => s + p.periodBillable, 0),
                          included: rest.reduce((s, p) => s + p.periodIncluded, 0),
                          nonBillable: rest.reduce((s, p) => s + p.periodNonBillable, 0),
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Panel>
        <Panel title={t('cockpit.alerts')} className="xl:col-span-2" bodyClassName="p-0">
          {alerts.length === 0 ? (
            <p className="p-4 text-sm text-graphite">{t('cockpit.noAlerts')}</p>
          ) : (
            <ul>
              {alerts.map((p) => (
                <li key={p.id} className="border-b border-line/70 px-4 py-2.5 last:border-0">
                  <button type="button" className="flex w-full flex-col gap-1.5 text-left hover:text-accent" onClick={() => navigate(`/projects/${p.id}?tab=kpi`)}>
                    <span className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate">
                        <span className="num font-medium">{p.number}</span> {p.name}
                      </span>
                      {Number.isFinite(p.budgetUsed) && (
                        <span className={clsx('num font-semibold', p.budgetUsed > 1 ? 'text-bad' : p.budgetUsed >= 0.8 ? 'text-warn' : 'text-graphite')}>{pct(p.budgetUsed)}</span>
                      )}
                    </span>
                    {p.budgetMinutes ? <Gauge value={p.totalMinutes} capacity={p.budgetMinutes} /> : null}
                    <span className="flex flex-wrap gap-1.5">
                      {Number.isFinite(p.budgetUsed) && p.budgetUsed >= 0.8 && <Pill tone={p.budgetUsed > 1 ? 'bad' : 'warn'}>{t('cockpit.budgetNote', { booked: h(p.totalMinutes, 0), budget: h(p.budgetMinutes, 0) })}</Pill>}
                      {p.overPlanTasks > 0 && <Pill tone="warn">{t('cockpit.overPlanNote', { count: p.overPlanTasks })}</Pill>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <Panel title={t('areas.title')}>
        <AreaBars areas={x.areas} hint={x.scope === 'lead' ? t('areas.hintLead') : t('areas.hintCockpit')} />
      </Panel>
      <Panel title={t('cockpit.weekly')}>
        <WeekBars
          showCapacity={false}
          data={x.weeks.map((w) => {
            const n = isoWeek(w.weekStart).week;
            return { label: `${t('kpi.weekPrefix')} ${n}`, axis: `${n}`, booked: w.minutes, billable: w.billable, capacity: 0 };
          })}
        />
      </Panel>
    </div>
  );
}

type SortKey = 'period' | 'total' | 'budget' | 'overPlan' | 'number';

function ProjectsTab({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const q = useQ('cockpit.overview', { from, to });
  const [sort, setSort] = useState<SortKey>('period');
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const list = (q.data?.projects ?? []).filter((p) => matchesSearch(`${p.number} ${p.name}`, search));
    const by: Record<SortKey, (a: (typeof list)[number], b: (typeof list)[number]) => number> = {
      period: (a, b) => b.periodMinutes - a.periodMinutes,
      total: (a, b) => b.totalMinutes - a.totalMinutes,
      budget: (a, b) => (Number.isFinite(b.budgetUsed) ? b.budgetUsed : -1) - (Number.isFinite(a.budgetUsed) ? a.budgetUsed : -1),
      overPlan: (a, b) => b.overPlanTasks - a.overPlanTasks,
      number: (a, b) => a.number.localeCompare(b.number),
    };
    return list.sort(by[sort]);
  }, [q.data, sort, search]);
  const chunk = useChunked(rows, 100, `${sort}|${search}|${from}|${to}`);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const billing = q.data.totals.unbilledMinutes !== null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="num text-xs text-graphite">{t('kpi.asOf', { from: d(q.data.since), to: d(q.data.asOf) })}</span>
        <Input className="ml-auto w-56" placeholder={t('cockpit.searchProject')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
        <label className="flex items-center gap-2 text-sm text-graphite">
          {t('cockpit.sort')}
          <Select className="w-52" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {(['period', 'total', 'budget', 'overPlan', 'number'] as const).map((k) => (
              <option key={k} value={k}>
                {t(`cockpit.sorts.${k}`)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {rows.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
      {rows.length > 0 && (
      <ScrollTable label={t('cockpit.tabs.projects')}>
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="border-b border-line bg-sheet-2">
            <tr>
              <th className={clsx(th, 'min-w-64')}>{t('common.project')}</th>
              <th className={clsx(th, 'text-right')}>{t('cockpit.colPeriod')}</th>
              <th className={clsx(th, 'text-right')}>{t('kpi.billable')}</th>
              <th className={clsx(th, 'text-right')}>{t('cockpit.colTotal')}</th>
              <th className={th}>{t('kpi.budgetUsed')}</th>
              <th className={clsx(th, 'text-right')}>{t('cockpit.colPlanTasks')}</th>
              <th className={clsx(th, 'text-right')}>{t('cockpit.colDone')}</th>
              {billing && <th className={clsx(th, 'text-right')}>{t('cockpit.colInvoiced')}</th>}
              {billing && <th className={clsx(th, 'text-right')}>{t('cockpit.unbilled')}</th>}
              <th className={clsx(th, 'text-right')}>{t('cockpit.approvalsPending')}</th>
            </tr>
          </thead>
          <tbody>
            {chunk.visible.map((p) => (
              <tr key={p.id} className="cursor-pointer border-b border-line/70 hover:bg-sheet-2" onClick={() => navigate(`/projects/${p.id}?tab=kpi`)}>
                <td className={td}>
                  <div className="font-medium">
                    <span className="num">{p.number}</span> {p.name}
                  </div>
                  <div className="text-xs text-graphite">
                    {t(`projects.billingMode.${p.billingMode as 'hourly'}`)}
                    {p.archived ? ` · ${t('common.archived')}` : ''}
                  </div>
                </td>
                <td className={clsx(td, 'num text-right')}>{h(p.periodMinutes, 1)}</td>
                <td className={clsx(td, 'num text-right')}>{h(p.periodBillable, 1)}</td>
                <td className={clsx(td, 'num text-right')}>{h(p.totalMinutes, 1)}</td>
                <td className={clsx(td, 'min-w-36')}>
                  {p.budgetMinutes ? (
                    <div className="flex flex-col gap-1">
                      <span className={clsx('num text-xs', p.budgetUsed > 1 ? 'font-semibold text-bad' : p.budgetUsed >= 0.8 ? 'font-semibold text-warn' : 'text-graphite')}>
                        {pct(p.budgetUsed)} {t('common.of')} {h(p.budgetMinutes, 0)} h
                      </span>
                      <Gauge value={p.totalMinutes} capacity={p.budgetMinutes} />
                    </div>
                  ) : (
                    <span className="text-graphite">-</span>
                  )}
                </td>
                <td className={clsx(td, 'num text-right whitespace-nowrap')}>
                  {p.tasksWithPlan}
                  {p.overPlanTasks > 0 && <Pill tone="warn" className="ml-1">{t('cockpit.overPlanShort', { count: p.overPlanTasks })}</Pill>}
                </td>
                <td className={clsx(td, 'num text-right whitespace-nowrap')} title={t('cockpit.colDoneHint')}>
                  {p.doneBookedMinutes > 0 ? (
                    <>
                      {h(p.donePlannedMinutes, 0)} / {h(p.doneBookedMinutes, 0)} h
                      <span className="block text-xs text-graphite">{pct(p.donePlannedMinutes / p.doneBookedMinutes)}</span>
                    </>
                  ) : (
                    '-'
                  )}
                </td>
                {billing && <td className={clsx(td, 'num text-right')}>{p.invoicedFactor !== null ? pct(p.invoicedFactor) : '-'}</td>}
                {billing && <td className={clsx(td, 'num text-right')}>{p.unbilledMinutes !== null ? h(p.unbilledMinutes, 1) : '-'}</td>}
                <td className={clsx(td, 'num text-right')}>{p.approvalsPending || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollTable>
      )}
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      <p className="text-xs text-graphite">{t('cockpit.projectsHint')}</p>
    </div>
  );
}
